/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Direction } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  OnChanges,
  SimpleChanges,
  NgZone,
  AfterViewInit,
  inject,
} from '@angular/core';
import { PriceModel } from '@app/shared/models/api/price.model';
import nouislider from 'nouislider';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { AmountPipe, TransformAmountService } from '@app/shared/pipes/amount.pipe';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { NgIf } from '@angular/common';

export interface SliderInputValue {
  min: PriceModel;
  max: PriceModel;
  disableMin: boolean;
  disableMax: boolean;
}

const MAX_AMOUNT = 6000;

@Component({
  selector: 'app-slider-input',
  templateUrl: './slider-input.template.html',
  styleUrls: ['./slider-input.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, NgIf, AmountPipe],
})
export class SliderInputComponent implements AfterViewInit, OnDestroy, OnChanges {
  public isBrowser: boolean;
  @Input() public value = 2500;
  @Input() public step = 10;
  @Input() public range: number[] = [];
  @Input() public currency?: string | null;
  @Input() public minAmount?: number | null;
  @Input() public maxAmount?: number | null;
  @Input() public ariaLabel?: string | null;
  @Input() public direction?: Direction | null;
  @Input() public showAmountLabel?: boolean | null = false;

  @Output()
  public readonly priceChanged: EventEmitter<SliderInputValue> = new EventEmitter();
  @Output() public readonly livePrice: EventEmitter<SliderInputValue> =
    new EventEmitter();

  @ViewChild('rangeSlider')
  public rangeSliderElement?: ElementRef | null;

  public sliderElement: any;
  public maxReached: boolean;
  public minReached: boolean;
  public selectedMinAmount: PriceModel;
  public selectedMaxAmount: PriceModel;

  /* eslint-disable @typescript-eslint/no-magic-numbers */

  private readonly RANGE_VALUES: Array<number> = [
    0, 3, 6, 8, 11, 14, 17, 19, 22, 25, 28, 31, 33, 36, 39, 42, 44, 47, 50, 55, 60, 65,
    70, 75, 80, 85, 90, 95, 100, 110, 120, 130, 140, 150, 160, 170, 180, 190, 200, 210,
    220, 230, 240, 250, 260, 270, 280, 290, 300, 350, 400, 450, 500, 600, 700, 800, 900,
    1000, 1250, 1500, 1750, 2000, 2250, 2500, 2750, 3000, 3500, 4000, 4500, 5000, 6000,
    7000, 8000, 9000, 10000, 12500, 15000, 17500, 20000,
  ];

  /* eslint-enable @typescript-eslint/no-magic-numbers */

  private readonly transformAmountService = inject(TransformAmountService);

  constructor(
    private readonly zone: NgZone,
    private readonly translateService: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.minReached = true;
    this.maxReached = true;
    this.selectedMinAmount = new PriceModel();
    this.selectedMaxAmount = new PriceModel();
  }

  public get slideOptions(): {
    behaviour: string;
    direction?: 'ltr' | 'rtl';
    start: Array<number>;
    animate: boolean;
    animationDuration: number;
    connect: boolean;
    margin: number;
    step: number;
    range: {
      min: number;
      max: number;
    };
  } {
    const options = {
      behaviour: 'drag',
      direction: this.direction || 'ltr',
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      start: [this.value],
      animate: true,
      animationDuration: 300,
      connect: [true, false] as any,
      margin: 0,
      step: this.step,
      range: {
        min: 1,
        max: this.maxAmount || this.RANGE_VALUES.length - 1,
      },
    };

    /*     if (options.start[1] < options.start[0]) {
      options.start[1] = options.start[0] + AMOUNT_PADDING;
    } */

    if (options.range.max < options.start[0]) {
      options.range.max = options.start[0];
    }

    return options;
  }

  public ngAfterViewInit(): void {
    this.refreshMinAmount();
    this.refreshMaxAmount();
    this.refreshCurrencies();

    if (this.isBrowser && this.rangeSliderElement?.nativeElement) {
      this.sliderElement = nouislider.create(
        this.rangeSliderElement.nativeElement,
        this.slideOptions
      );
    } else {
      this.sliderElement = {
        on: () => {},
        off: () => {},
        updateOptions: () => {},
      };
    }

    this.sliderElement.on('change', (values: string[]) => {
      this.zone.run(() => {
        this.sliderValueUpdated(values, true);
      });
    });

    this.sliderElement.on('update', (values: string[]) => {
      this.zone.run(() => {
        this.sliderValueUpdated(values, false);
      });
    });
  }

  public sliderValueUpdated(values: Array<string>, isFinal = false): void {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (values) {
      let highestNum = parseInt(values[0] || '0', 10);

      if (highestNum < 0 || isNaN(highestNum)) {
        highestNum = 0;
      }

      // Updated the user selected max amount
      this.selectedMaxAmount = new PriceModel();
      this.selectedMaxAmount.amount = this.translateAmountToRangeAmount(highestNum);
      this.selectedMaxAmount.currency = this.currency || 'USD';

      this.minReached = Boolean(highestNum <= 0);
      this.maxReached = Boolean(
        this.translateAmountToRangeAmount(highestNum) >=
          (this.maxAmount || this.RANGE_VALUES.length - 1)
      );
      this.changeDetector.markForCheck();
      this.changeDetector.detectChanges();

      this.livePrice.emit({
        min: this.selectedMinAmount,
        max: this.selectedMaxAmount,
        disableMax: this.maxReached,
        disableMin: this.minReached,
      });

      if (isFinal) {
        this.priceChanged.emit({
          min: this.selectedMinAmount,
          max: this.selectedMaxAmount,
          disableMax: this.maxReached,
          disableMin: this.minReached,
        });
      }
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.minAmount || changes.maxAmount || changes.currency || changes.value) {
      if (changes.minAmount && changes.minAmount.isFirstChange()) {
        this.refreshMinAmount();
      }

      if (changes.maxAmount) {
        this.refreshMaxAmount();
      }

      if (changes.currency && changes.currency.isFirstChange()) {
        this.refreshCurrencies();
      }

      this.valuesUpdated();
    }
  }

  private refreshMinAmount(): void {}

  private refreshMaxAmount(): void {
    this.selectedMaxAmount.amount = this.translateAmountToRangeAmount(
      this.selectedMaxAmount.amount
    );
  }

  private refreshCurrencies(): void {
    this.selectedMinAmount.currency = this.currency || this.selectedMinAmount.currency;
    this.selectedMaxAmount.currency = this.currency || this.selectedMaxAmount.currency;
  }

  public valuesUpdated(): void {
    if (this.sliderElement) {
      const options = {
        behaviour: 'drag',
        direction: this.direction || 'ltr',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        start: [this.value],
        animate: true,
        animationDuration: 300,
        connect: [true, false] as any,
        margin: 0,
        step: this.step,
        range: {
          min: 1,
          max: this.maxAmount || this.RANGE_VALUES.length - 1,
        },
      };
      const sliderOptions = options;
      this.sliderElement.updateOptions(
        {
          start: sliderOptions.start,
          range: sliderOptions.range,
        },
        false
      );
      this.changeDetector.markForCheck();
    }
  }

  public translateAmountToRangeAmount(amount: number): number {
    if (this.range.length > 0) {
      for (let i = 0; i < this.range.length; i += 1) {
        if (amount <= this.range[i]) {
          return this.range[i];
        }
      }
    }

    return amount;
  }

  public get emptyAmount(): PriceModel {
    const price = new PriceModel();
    price.amount = 0;
    price.currency = this.currency || 'USD';
    return price;
  }

  public get maxAmountLabel(): string {
    const price = new PriceModel();
    price.amount = this.maxAmount || MAX_AMOUNT;
    price.currency = this.currency || 'USD';

    return this.translateService.translate('label_price_range_more_than', {
      var1: this.transformAmountService.transform(price),
    });
  }

  public ngOnDestroy(): void {
    if (this.sliderElement) {
      this.sliderElement.off();

      if (this.sliderElement.noUiSlider && this.sliderElement.noUiSlider.destroy) {
        this.sliderElement.noUiSlider.destroy();
      }
    }
  }
}
